import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ApiStatusBadge } from '@/components/api-status-badge'
import { LogoMark } from '@/components/logo'
import { NavIcon } from '@/components/nav-icons'
import { Sidebar } from '@/components/sidebar'
import { ThemeToggle } from '@/components/theme-toggle'
import { SessionBadge } from '@/features/auth/session-badge'
import { useSidebar } from '@/features/layout/use-sidebar'

const RAIL_ID = 'app-rail'
const DRAWER_ID = 'app-drawer'

export function AppLayout() {
  const { collapsed, toggle } = useSidebar()

  /*
   * The drawer's open state is NOT persisted and NOT shared with the fold: one
   * is a layout preference on a desk, the other is a momentary overlay on a
   * phone, and a drawer that came back open on the next visit would be a page
   * somebody has to dismiss before they can read it.
   */
  const [drawerOpen, setDrawerOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const drawer = useRef<HTMLElement>(null)
  const wasOpen = useRef(false)
  const location = useLocation()

  // Any navigation closes it — including Back, and a redirect from a link that
  // no `onClick` would ever see.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the pathname is the trigger, not an input
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (drawerOpen) {
      // Into the drawer, so the next Tab lands inside it rather than on
      // something underneath the overlay.
      drawer.current?.focus()

      const onKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') setDrawerOpen(false)
      }
      window.addEventListener('keydown', onKey)
      wasOpen.current = true
      return () => window.removeEventListener('keydown', onKey)
    }

    // Back to where it was opened from — but only after a real close. On the
    // first render this branch runs too, and stealing focus on page load is
    // how a keyboard user ends up somewhere they never went.
    if (wasOpen.current) {
      wasOpen.current = false
      menuButton.current?.focus()
    }
  }, [drawerOpen])

  return (
    <div className="flex h-full">
      <Sidebar variant="rail" id={RAIL_ID} collapsed={collapsed} onToggle={toggle} />

      {/*
       * ALWAYS MOUNTED, so it can slide: an element that only exists while open
       * has no "before" to animate from. `inert` is what makes that safe — a
       * closed drawer parked off-screen is otherwise six focusable links a
       * keyboard user tabs through without seeing any of them.
       */}
      <div
        className={`fixed inset-0 z-40 sm:hidden ${drawerOpen ? '' : 'pointer-events-none'}`}
        inert={!drawerOpen}
      >
        {/* The scrim closes on a tap, which is what a finger expects. It is
            skipped by Tab: the labelled close button inside says the same. */}
        <button
          type="button"
          tabIndex={-1}
          aria-label="Navigation schließen"
          onClick={() => setDrawerOpen(false)}
          className={`bg-ink/40 absolute inset-0 transition-opacity duration-200 ${
            drawerOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`absolute inset-y-0 left-0 transition-transform duration-200 ease-out ${
            drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <Sidebar
            variant="drawer"
            id={DRAWER_ID}
            ref={drawer}
            open={drawerOpen}
            onToggle={() => setDrawerOpen(false)}
            onNavigate={() => setDrawerOpen(false)}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Wraps rather than overflows: a second row is honest where a clipped
            control would not be. The API status is the dot alone here — its
            wording moved into the drawer, which paid for the menu button. */}
        <header className="border-line bg-raised/85 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-2 py-2 backdrop-blur sm:hidden">
          <span className="flex items-center gap-1">
            <button
              ref={menuButton}
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Navigation öffnen"
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              aria-controls={DRAWER_ID}
              // 44px square: this is pressed with a thumb, often a gloved one.
              className="text-muted hover:text-ink flex size-11 items-center justify-center transition-colors"
            >
              <NavIcon name="menu" className="size-5" />
            </button>
            <LogoMark className="h-5 w-auto" />
            <span className="font-display ml-1 text-[14px] tracking-tight">
              aCasa<span className="text-brand">Immobilien</span>
            </span>
          </span>
          <span className="flex items-center gap-3 pr-2">
            <ThemeToggle />
            <ApiStatusBadge compact />
            {/* Initials only — a full name would not fit. */}
            <SessionBadge compact />
          </span>
        </header>

        {/* Full-bleed: the document matrix needs every pixel of width. */}
        <main className="min-w-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
